'use client';
import { Box, Card, CardActionArea, CardContent, Stack, Typography, useTheme } from '@mui/material';
import NextLink from 'next/link';
import React from 'react';

import { ParsePillHeading } from '@/components/ParsePillHeading';
import { formatDate } from '@/lib/DateHelpers';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import {
  ComponentBlocksRelatedBlogs,
  Enum_Componentshareddisplayoptions_Contentswidth,
  ResourceArticle,
} from '@/types';

import { CustomBackground } from '../CustomBackground/CustomBackground';

type Blog = ResourceArticle;

const CategoryBadge = ({ name }: { name: string }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        alignSelf: 'flex-start',
        px: 0.8,
        py: 0.25,
        borderRadius: '8px',
        backgroundColor: theme.palette.purple[500],
        color: theme.palette.purple[900],
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      {name}
    </Box>
  );
};

const BlogMeta = ({ author, date, color }: { author?: string; date?: string; color?: string }) => {
  const theme = useTheme();
  if (!author && !date) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
      {author && (
        <Typography
          component="span"
          sx={{
            fontSize: '0.8rem',
            fontWeight: 500,
            color: color ?? theme.palette.text.primary,
          }}
        >
          {author}
        </Typography>
      )}
      {date && (
        <Typography
          component="span"
          sx={{
            fontSize: '0.8rem',
            color: color ?? theme.palette.text.secondary,
          }}
        >
          {formatDate(date)}
        </Typography>
      )}
    </Stack>
  );
};

export default function RecentBlogs({
  Blogs,
  HighlightBlog,
  RelatedBlogsBackground,
  RelatedBlogsDisplayOptions,
}: ComponentBlocksRelatedBlogs) {
  const theme = useTheme();
  if (!Blogs?.length) return null;

  const allBlogs = HighlightBlog
    ? [HighlightBlog, ...Blogs.filter((b) => b?.Slug !== HighlightBlog.Slug)]
    : Blogs;

  const highlight = allBlogs[0];
  const sideBlogs = allBlogs.slice(1, 4);

  const renderHighlight = (blog: Blog) => (
    <Card
      key={blog.Slug}
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        boxShadow: theme.shadows[2],
        p: 0,
      }}
    >
      <CardActionArea
        component={NextLink}
        href={`/resources/${blog.ResourceCategory?.Slug || ''}/${blog.Slug}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: theme.palette.purple[900],
        }}
      >
        {/* Image */}
        <Box
          component="img"
          src={
            getImageUrl({ image: blog.HeroImage, size: 'url' }) ||
            getImageUrl({ image: blog.ResourceThumbnail, size: 'url' }) ||
            getImageUrl({ image: blog.seo?.metaImage, size: 'url' }) ||
            '/assets/placeholder-thumbnail.png'
          }
          alt={blog.HeroImage?.alternativeText || blog.ResourceTitle}
          sx={{
            width: '100%',
            height: { xs: 240, sm: 340 },
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />

        {/* Content */}
        <CardContent
          sx={{
            p: 3,
            color: theme.palette.common.white,
            backgroundColor: theme.palette.purple[800],

            display: 'flex',
            flexDirection: 'column',

            flexGrow: 1,
            height: '100%',
          }}
        >
          {blog.ResourceCategory?.CategoryName && (
            <CategoryBadge name={blog.ResourceCategory.CategoryName} />
          )}

          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              color: theme.palette.common.white,
              mb: 1,
              lineHeight: 1.3,
            }}
          >
            {blog.ResourceTitle}
          </Typography>

          {blog.ResourceDescription && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.grey[200],
                mb: 2,
                lineHeight: 1.6,
                flexGrow: 1,
              }}
            >
              {blog.ResourceDescription}
            </Typography>
          )}

          {/* Push meta to bottom */}
          <Box sx={{ mt: 'auto' }}>
            {' '}
            <BlogMeta
              author={blog?.Author?.Name || ''}
              date={blog.publishedAt}
              color={theme.palette.grey[300]}
            />
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );

  const renderSideBlog = (blog: Blog) => (
    <Card
      key={blog.Slug}
      sx={{
        display: 'flex',
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: theme.palette.purple[200],
        boxShadow: theme.shadows[1],
        p: 0,
      }}
    >
      <CardActionArea
        component={NextLink}
        href={`/resources/${blog.ResourceCategory?.Slug || ''}/${blog.Slug}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'flex',
          width: '100%',
        }}
      >
        {/* Thumbnail */}
        <Box
          component="img"
          src={
            getImageUrl({ image: blog.ResourceThumbnail, size: 'url' }) ||
            getImageUrl({ image: blog.HeroImage, size: 'url' }) ||
            getImageUrl({ image: blog.seo?.metaImage, size: 'url' }) ||
            '/assets/placeholder-thumbnail.png'
          }
          alt={blog.HeroImage?.alternativeText || blog.ResourceTitle}
          sx={{
            width: 120,
            height: '100%',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />

        {/* Text */}
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: 2,
          }}
        >
          {blog.ResourceCategory?.CategoryName && (
            <CategoryBadge name={blog.ResourceCategory.CategoryName} />
          )}

          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{
              color: theme.palette.purple[900],
              lineHeight: 1.4,
              mb: 0.5,
            }}
          >
            {blog.ResourceTitle}
          </Typography>

          <BlogMeta
            author={blog?.Author?.Name || ''}
            date={blog.publishedAt}
            color={theme.palette.text.secondary}
          />
        </CardContent>
      </CardActionArea>
    </Card>
  );

  return (
    <CustomBackground
      backgroundOptions={(RelatedBlogsBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        (RelatedBlogsDisplayOptions ?? {
          ContentsWidth: Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: 'md',
          PaddingBottom: 'md',
          PaddingInline: 'md',
        }) as StrapiDisplayOptions | undefined
      }
      boxProps={{
        // @ts-expect-error - actually does support data props
        'data-component': 'RelatedBlogs',
      }}
    >
      <Box
        sx={{
          mx: 'auto',
        }}
      >
        <ParsePillHeading title="_Recent_ Blogs" />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 3, md: 4 }}
          alignItems="stretch"
          sx={{ mt: 4 }}
        >
          {/* Left: Highlight */}
          <Box sx={{ width: { xs: '100%', md: '60%' } }}>
            {highlight && renderHighlight(highlight)}
          </Box>

          {/* Right: Side Blogs */}
          <Stack
            sx={{
              width: { xs: '100%', md: '40%' },
              gap: 2,
              justifyContent: 'flex-start',
            }}
          >
            {sideBlogs.map((b) => renderSideBlog(b as Blog))}
          </Stack>
        </Stack>
      </Box>
    </CustomBackground>
  );
}
